import React, { useState, useMemo } from 'react';
import { connect } from 'react-redux';
import { withStyles } from '@material-ui/core/styles';
import { Paper, Typography, Tooltip, IconButton, Fab } from '@material-ui/core';
import { BackButton, hasPermission, localization, hasAtLeastOnePermission, getDimensionsOfPagination } from '../../sc-utilities';
import MaterialTable from 'material-table';
import * as actions from './store/typicalProblemActions';
import * as action from '../../np-core/Auth/store/authActions';
import { Refresh, Add, Delete, CloudUpload } from '@material-ui/icons';
import TypicalProblemDelete from './TypicalProblemDelete';
import Toolbar from '../../sc-utilities/Toolbar';
import { MuiTableRows } from '../../../themes';
import * as Yup from 'yup';
import Export from '../../sc-components/ExportForm/';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';

const styles = theme => ({
    "@global": {
        "[class*='MuiTableCell-root MuiTableCell-body']": {
            height: "40px",
            padding: '2px',
            fontSize: '0.7rem'
        },
    },
    fab: {
        textTransform: 'none',
        color: theme.palette.buttonText.default,
        marginLeft: theme.spacing(),
        backgroundColor: theme.palette.primary.main,
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
        }
    },
    tableTitle: {
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'center',
        overflow: 'visible',
    },
    customTooltip: {
        marginLeft: -16
    },
    customToolbar: {
        marginTop: 9,
        marginLeft: 14,
        width: "900px"
    }
});

let pageSize = 100;
let rowsPageSizeInit = [20, 50, 100];
let initialValues = [
    'Name',
    'LongName',
    'Code',
    'System',
    'ServiceType',
    'GradeID',
    'GradingCategoryID',
    'Visible',
];

const route = 'typical-problems';

const TypicalProblemList = props => {
    const { classes, translations, deleting, onFetchTypicalProblem, onFetchTypicalProblemCount, saveColumnsFilter, activeCustomerID, tableColumns, onBulkDeleteTypicalProblem, permissions, redirectHandler, onBulkVisibilityEditTypicalProblem, tableRef, goToTypicalProblemsUpload } = props;
    const columnObject = useMemo(() => tableColumns, [tableColumns]);
    const [deleteModalState, setDeleteModalState] = useState({ open: false, typicalProblemIDs: [] });
    const selectionPermissions = hasAtLeastOnePermission(permissions, ['typical_problem_delete', 'typical_problem_visibility_update', 'system_typical_problem_delete']);
    const windowHeight = hasPermission(permissions, 'typical_problem_export') ? 330 : 290;

    const [tableOptions, setTableOptions] = useState({
        filtering: true,
        sorting: false,
        selection: selectionPermissions,
        fetching: true,
        selectionProps: rowData => {
            let disabled = !selectionPermissions;
            rowData.tableData.disabled = disabled
            return disabled ? {
                disabled: true,
                checked: false
            } : {}
        },
        search: false,
        minBodyHeight: window.innerHeight - windowHeight < 300 ? 300 : window.innerHeight - windowHeight,
        maxBodyHeight: window.innerHeight - windowHeight,
        pageSizeOptions: rowsPageSizeInit,

        exportButton: false,
        columnsButton: true,
        rowStyle: (_rowData, index) => {
            return { backgroundColor: index % 2 ? MuiTableRows.odd : MuiTableRows.even };
        },
        pageSize: pageSize ? pageSize : 50,
        emptyRowsWhenPaging: false
    });

    const filterQuery = filters => {
        let obj = {};
        for (var filter of filters) {
            obj[filter.column.field] = filter.value;
        }
        return obj;
    }

    async function fetchTableData(query) {
        let q = filterQuery(query.filters);
        query['ActiveCustomerID'] = activeCustomerID;
        query['q'] = q;
        let count = await onFetchTypicalProblemCount(query);
        if (!query['page'] && query['pageSize'] !== 20 && query['pageSize'] !== 100) {
            let dimensions = getDimensionsOfPagination(count)
            query['pageSize'] = dimensions.pageSize
            rowsPageSizeInit = dimensions.rowsPageSize
            pageSize = dimensions.pageSize
        }
        let result = count ? await onFetchTypicalProblem(query) : [];
        setTableOptions({ ...tableOptions, pageSize: query['pageSize'], pageSizeOptions: rowsPageSizeInit, fetching: false });
        return { data: result, page: query.page, pageSize: pageSize, totalCount: count };
    }

    const tableTitle = () => {
        return <div className={classes.customToolbar}>
            <div className={classes.tableTitle}>
                {BackButton(translations, classes.customTooltip)}
                <Typography className={classes.typography}>{translations["typical_problems"] || 'Typical problems'}</Typography>
                {hasPermission(permissions, 'typical_problem_create') ? <Fab
                    variant="extended"
                    size="small"
                    color="primary"
                    aria-label="Add"
                    style={{ borderRadius: '1px' }}
                    className={classes.fab}
                    onClick={() => redirectHandler(`/settings/typical-problem/create`)}
                >
                    <Add size="small" />
                    {translations["create_new"] || "Create new"}
                </Fab> : null}
                <Tooltip title={translations["refresh"] || "Refresh"}>
                    <IconButton aria-label={translations["refresh"] || "Refresh"} component="div" disabled={tableOptions.fetching} onClick={() => tableRef.current && tableRef.current.onQueryChange()}>
                        <Refresh />
                    </IconButton>
                </Tooltip>
            </div>
            <>
                {
                    hasPermission(permissions, "typical_problem_import") ? (
                        <Tooltip title={translations["import_typical_problems"] || "Import Typical Problems"}>
                            <IconButton aria-label={translations["import_typical_problems"] || "Import Typical Problems"} value='csv'
                                onClick={() => goToTypicalProblemsUpload()}>
                                <CloudUpload />
                            </IconButton>
                        </Tooltip>
                    ) : null
                }
                {
                    hasPermission(permissions, 'typical_problem_export') ?
                        <Export
                            route={route}
                            tableRef={tableRef}
                            initialValues={initialValues}
                            formSettings={formSettings}
                            showVisibilities={true}
                            showTypes={true}
                        /> : null
                }


            </>
        </div>
    }

    const columnHidden = newColumnState => {
        let objToSendColumn = {}
        let objColumns = {}
        columnObject.map(column => {
            objColumns[`${column.field}`] = column.hidden
            return objColumns
        })
        objToSendColumn[`TypicalProblem`] = objColumns
        saveColumnsFilter({ ...objToSendColumn, ActiveCustomerID: activeCustomerID })
        return newColumnState
    }

    const handleCloseSafe = () => {
        setDeleteModalState({ open: false, typicalProblemIDs: [] });
    }

    const handleCloseDanger = async () => {
        await onBulkDeleteTypicalProblem({ TypicalProblemIDs: deleteModalState.typicalProblemIDs, ActiveCustomerID: activeCustomerID });
        setDeleteModalState({ open: false, typicalProblemIDs: [] });
        tableRef.current && tableRef.current.onQueryChange();

    };

    const handleBulkVisibility = async (visibleInvisibleTypicalProblemIDs, visibility) => {
        await onBulkVisibilityEditTypicalProblem({ ActiveCustomerID: activeCustomerID, TypicalProblemIDs: visibleInvisibleTypicalProblemIDs, Visible: visibility });
        tableRef.current && tableRef.current.onQueryChange();
    };

    const handleBulkDelete = typicalProblemIDs => {
        setDeleteModalState({ ...deleteModalState, open: true, typicalProblemIDs: typicalProblemIDs });
    };

    const tableActions = [
        {
            tooltip: `${translations["remove_all_selected"] || "Remove all selected"} ${translations["typical_problems"] || 'Typical problems'}`,
            icon: <Delete />,
            onClick: (rowData) => handleBulkDelete(rowData.map(typicalProblem => typicalProblem.TypicalProblemID))
        },
        {
            tooltip: `${translations["typical_problem_visibility_on"] || "Visibility on to all selected"} ${translations["typical_problems"] || 'Typical problems'}`,
            icon: <VisibilityIcon />,
            onClick: (rowData) => handleBulkVisibility(rowData.map(typicalProblem => typicalProblem.TypicalProblemID), true)
        },
        {
            tooltip: `${translations["typical_problem_visibility_off"] || "Visibility off to all selected"} ${translations["typical_problems"] || 'Typical problems'}`,
            icon: <VisibilityOffIcon />,
            onClick: (rowData) => handleBulkVisibility(rowData.map(typicalProblem => typicalProblem.TypicalProblemID), false)
        }
    ];

    let formSettings = {
        Validation: initialValues.reduce((o, k) => ({ ...o, [k]: Yup.boolean() }), {}),
        ButtonSubmit: hasPermission(permissions, "typical_problem_export") ? {
            Title: translations["download"] || "Download",
            Color: 'primary'
        } : null
    };

    return (
        <Paper>
            <MaterialTable
                tableRef={tableRef}
                title={tableTitle()}
                columns={columnObject}
                data={fetchTableData}
                options={tableOptions}
                isLoading={tableOptions.fetching}
                components={{
                    Toolbar: props => (
                        <Toolbar data={props} actions={tableActions} selectedRows={props.selectedRows} translations={translations} />
                    )
                }}
                localization={localization(translations)}
                onChangeRowsPerPage={(size) => {
                    pageSize = size
                }}
                onChangeColumnHidden={columnHidden}
                onChangePage={() => {if(tableRef?.current?.tableContainerDiv?.current) {tableRef.current.tableContainerDiv.current.scrollTop = 0}}}
            />
            {hasPermission(permissions, 'typical_problem_delete') ? (
                <TypicalProblemDelete
                    activeCustomerID={activeCustomerID}
                    entityName={translations["typical_problems"] || 'Typical problems'}
                    translations={translations}
                    deleting={deleting}
                    handleCloseDanger={handleCloseDanger}
                    handleCloseSafe={handleCloseSafe}
                    deleteModalState={deleteModalState}
                />
            ) : null}
        </Paper>
    );
};

const mapStateToProps = state => {
    return {
        activeCustomerID: state?.core?.login?.activeCustomer?.CustomerID,
        translations: state?.translate?.translations?.activeTranslations || {},
        deleting: state?.setting?.typicalProblems?.deleting,
        permissions: state?.core?.login?.permissions || []
    };
};

const mapDispatchToProps = dispatch => {
    return {
        onFetchTypicalProblemCount: filter => dispatch(actions.fetchTypicalProblemCount(filter)),
        onFetchTypicalProblem: filter => dispatch(actions.fetchTypicalProblem(filter)),
        saveColumnsFilter: data => dispatch(action.saveColumnsFilter(data)),
        onBulkDeleteTypicalProblem: data => dispatch(actions.bulkDeleteTypicalProblems(data)),
        onBulkVisibilityEditTypicalProblem: data => dispatch(actions.bulkVisibilityEditTypicalProblems(data)),
    }
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(withStyles(styles, { withTheme: true })(TypicalProblemList));


